import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, Outlet, RouterProvider } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ThemeProvider, useTheme } from "./hooks/useTheme";
import { PointsProvider } from "./contexts/PointsContext";
import { ToastProvider, useToast } from "./contexts/ToastContext";

import { useAuth } from "./hooks/useAuth";
import "./i18n";

import useFeedbackTrigger from "./hooks/useFeedbackTrigger";

import { createAppRouter, useIsMobile } from "./router";
import { getRouteSeo, applyRouteSeo } from "./seo";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ConnectionStatusBanner from "./components/ConnectionStatusBanner";
import AdminAnnouncementBanner from "./components/AdminAnnouncementBanner";
import RatingPopup from "./components/RatingPopup";
import ErrorReportPopup from "./components/ErrorReportPopup";
import OrrithFeedbackSystem from "./components/OrrithFeedbackSystem";
import PWAUpdateBanner from "./components/PWAUpdateBanner";
import PWAUpdateDialog from "./components/PWAUpdateDialog";

function RootLayout({ darkMode, setDarkMode, user, mobile, setMobile }) {
  const location = useLocation();
  const isOrrithLanding = location.pathname === "/";
  const seoConfig = useMemo(() => getRouteSeo(location.pathname), [location.pathname]);

  const {
    currentPopup,
    dismissCurrentPopup,
    errorToReport,
    dismissError,
  } = useFeedbackTrigger(user);

  useEffect(() => {
    if (mobile) setMobile(false);
  }, [location.pathname, mobile, setMobile]);

  useEffect(() => {
    applyRouteSeo(seoConfig);
  }, [seoConfig]);

  return (
    <div className={`min-h-screen flex flex-col ${isOrrithLanding ? "dark" : ""}`}>
      <ConnectionStatusBanner />

      <Navbar
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        user={user}
        setMobile={setMobile}
      />

      <AdminAnnouncementBanner />

      <main className="flex-1 min-h-[60vh]">
        <Outlet />
      </main>

      <Footer />

      {currentPopup && (
        <RatingPopup popup={currentPopup} onDismiss={dismissCurrentPopup} user={user} />
      )}

      {errorToReport && (
        <ErrorReportPopup
          isOpen={!!errorToReport}
          onClose={dismissError}
          error={errorToReport}
          user={user}
        />
      )}

      <OrrithFeedbackSystem mode="floating" />
    </div>
  );
}

function AppInner() {
  const { user, loading: authLoading } = useAuth();
  const [mobile, setMobile] = useState(false);
  const { setTheme, isDark } = useTheme();
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const toast = useToast();

  // PWA Update state
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);
  const waitingWorkerRef = useRef(null);

  const darkMode = Boolean(isDark);

  const setDarkMode = useCallback((flag) => {
    setTheme(flag ? "dark" : "light");
  }, [setTheme]);

  useEffect(() => {
    const currentLang = i18n.language || "en";
    document.documentElement.lang = currentLang.split("-")[0];
  }, [i18n.language]);

  // PWA Update handling
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleSWUpdate = (event) => {
      if (event?.detail?.waitingWorker) {
        waitingWorkerRef.current = event.detail.waitingWorker;
      }
      setUpdateAvailable(true);
    };

    const handleShowDialog = () => {
      setShowUpdateDialog(true);
    };

    window.addEventListener("swupdate", handleSWUpdate);
    window.addEventListener("pwa-update-show-dialog", handleShowDialog);

    // Check if there's already a waiting worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          waitingWorkerRef.current = registration.waiting;
          setUpdateAvailable(true);
        }
      });
    }

    return () => {
      window.removeEventListener("swupdate", handleSWUpdate);
      window.removeEventListener("pwa-update-show-dialog", handleShowDialog);
    };
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!waitingWorkerRef.current) return;

    setIsApplyingUpdate(true);
    setShowUpdateDialog(false);

    try {
      // Tell the waiting worker to skip waiting and activate
      waitingWorkerRef.current.postMessage({ type: "SKIP_WAITING" });

      // Wait for controller change then reload
      await new Promise((resolve) => {
        const onControllerChange = () => {
          navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
          resolve();
        };
        navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

        // Fallback timeout
        setTimeout(resolve, 3000);
      });

      window.location.reload();
    } catch (error) {
      console.error("[PWA] Update failed:", error);
      setIsApplyingUpdate(false);
      setUpdateAvailable(true);
      toast.error("Update failed", {
        message: "Could not apply update. Please refresh manually.",
      });
    }
  }, [toast]);

  const handleLater = useCallback(() => {
    setUpdateAvailable(false);
    setShowUpdateDialog(false);
    // The update will be re-checked on next visit or when SW checks again
  }, []);

  const handleDismissBanner = useCallback(() => {
    setUpdateAvailable(false);
    setShowUpdateDialog(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleDismiss = () => {
      setUpdateAvailable(false);
      setShowUpdateDialog(false);
    };

    window.addEventListener("pwa-update-dismiss", handleDismiss);
    return () => window.removeEventListener("pwa-update-dismiss", handleDismiss);
  }, []);

  const router = useMemo(
    () =>
      createAppRouter({
        layoutElement: (
          <RootLayout
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            user={user}
            mobile={mobile}
            setMobile={setMobile}
          />
        ),
        darkMode,
        setDarkMode,
        user,
        mobile,
        setMobile,
        isMobile,
      }),
    [darkMode, setDarkMode, user, mobile, setMobile, isMobile]
  );

  return (
    <>
      <RouterProvider router={router} future={{ v7_startTransition: true, v7_relativeSplatPath: true }} />
      <PWAUpdateBanner
        isVisible={updateAvailable}
        onUpdate={handleUpdate}
        onDismiss={handleDismissBanner}
        onLater={handleLater}
        showDialogTrigger={true}
      />
      <PWAUpdateDialog
        isOpen={showUpdateDialog}
        onClose={() => setShowUpdateDialog(false)}
        onApply={handleUpdate}
        onLater={handleLater}
        isApplying={isApplyingUpdate}
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider position="bottom-right">
        <PointsProvider>
          <AppInner />
        </PointsProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
